'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useLocale } from 'next-intl';
import { useLocalizedPath } from '@/src/hooks/useLocalizedPath';
import { useAuth } from '@/src/context/AuthContext';
import { useTurnstile } from '@/src/hooks/useTurnstile';
import { resolveErrorMessage } from '@/src/utils/errorInterpolate';
import { readReferralCookie, clearReferralCookie } from '@/src/lib/referral';
import GoogleSignInButton from './GoogleSignInButton';
import FacebookSignInButton from './FacebookSignInButton';
import GithubSignInButton from './GithubSignInButton';
import TwitterSignInButton from './TwitterSignInButton';
import styles from './Auth.module.css';

interface RegisterFormProps {
  translations: {
    title: string;
    subtitle: string;
    usernameLabel: string;
    usernamePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    passwordLabel: string;
    passwordPlaceholder: string;
    confirmPasswordLabel: string;
    confirmPasswordPlaceholder: string;
    passwordMismatch: string;
    firstNameLabel: string;
    firstNamePlaceholder: string;
    lastNameLabel: string;
    lastNamePlaceholder: string;
    acceptTerms: string;
    termsLink: string;
    submit: string;
    submitting: string;
    hasAccount: string;
    login: string;
    successMessage: string;
    orDivider: string;
    googleSignUp: string;
    facebookSignUp: string;
    githubSignUp: string;
    twitterSignUp: string;
    errors: Record<string, string>;
  };
}

const RegisterForm: React.FC<RegisterFormProps> = ({ translations }) => {
  const locale = useLocale();
  const { localizeFullPath } = useLocalizedPath();
  const { register } = useAuth();
  const turnstile = useTurnstile({ action: 'register' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [referral, setReferral] = useState<string | undefined>(undefined);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    acceptTerms: false,
  });

  useEffect(() => {
    setReferral(readReferralCookie());
  }, []);

  const nameRegex = /^[a-zA-Z\s'-]*$/;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if ((name === 'firstName' || name === 'lastName') && !nameRegex.test(value)) return;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (turnstile.isRequired && !turnstile.token) {
      setError(translations.errors.DEFAULT);
      return;
    }
    setIsSubmitting(true);
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError(translations.passwordMismatch);
      setIsSubmitting(false);
      return;
    }

    try {
      await register({
        username: formData.username,
        email: formData.email,
        phoneNumber: formData.phoneNumber || undefined,
        password: formData.password,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        referral: readReferralCookie(),
        acceptTerms: formData.acceptTerms,
        locale,
        turnstileToken: turnstile.token ?? undefined,
      });
      clearReferralCookie();
      setSuccess(true);
    } catch (err) {
      turnstile.reset();
      setError(resolveErrorMessage(err, translations.errors));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.authPage}>
      <div className={styles.authCard}>
        <div className={styles.authLogo}>
          <Link href="/">
            <img src="/short-logo-black.svg" alt="ACEB" />
          </Link>
        </div>
        <h1 className={styles.authTitle}>{translations.title}</h1>
        <p className={styles.authSubtitle}>{translations.subtitle}</p>

        {error && (
          <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
        )}

        {success && (
          <div className={`${styles.alert} ${styles.alertSuccess}`}>{translations.successMessage}</div>
        )}

        {!success && (
          <form className={styles.authForm} onSubmit={handleSubmit}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="reg-firstname">
                  {translations.firstNameLabel}
                </label>
                <input
                  id="reg-firstname"
                  className={styles.formInput}
                  type="text"
                  name="firstName"
                  placeholder={translations.firstNamePlaceholder}
                  value={formData.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="reg-lastname">
                  {translations.lastNameLabel}
                </label>
                <input
                  id="reg-lastname"
                  className={styles.formInput}
                  type="text"
                  name="lastName"
                  placeholder={translations.lastNamePlaceholder}
                  value={formData.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="reg-username">
                {translations.usernameLabel}
              </label>
              <input
                id="reg-username"
                className={styles.formInput}
                type="text"
                name="username"
                placeholder={translations.usernamePlaceholder}
                value={formData.username}
                onChange={handleChange}
                autoComplete="username"
                required
                minLength={4}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="reg-email">
                {translations.emailLabel}
              </label>
              <input
                id="reg-email"
                className={styles.formInput}
                type="email"
                name="email"
                placeholder={translations.emailPlaceholder}
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="reg-phone">
                {translations.phoneLabel}
              </label>
              <input
                id="reg-phone"
                className={styles.formInput}
                type="tel"
                name="phoneNumber"
                placeholder={translations.phonePlaceholder}
                value={formData.phoneNumber}
                onChange={handleChange}
                autoComplete="tel"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="reg-password">
                {translations.passwordLabel}
              </label>
              <div className={styles.passwordToggle}>
                <input
                  id="reg-password"
                  className={styles.formInput}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={translations.passwordPlaceholder}
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className={styles.passwordToggleButton}
                  onClick={() => setShowPassword(prev => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="reg-confirm-password">
                {translations.confirmPasswordLabel}
              </label>
              <div className={styles.passwordToggle}>
                <input
                  id="reg-confirm-password"
                  className={`${styles.formInput}${formData.confirmPassword && formData.password !== formData.confirmPassword ? ` ${styles.formInputError}` : ''}`}
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder={translations.confirmPasswordPlaceholder}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                />
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className={styles.fieldError}>{translations.passwordMismatch}</p>
              )}
            </div>

            <div className={styles.checkboxGroup}>
              <input
                id="reg-terms"
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                required
              />
              <label htmlFor="reg-terms">
                {translations.acceptTerms}{' '}
                <Link href="/terms">{translations.termsLink}</Link>
              </label>
            </div>

            {turnstile.widget}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting || (turnstile.isRequired && !turnstile.token)}
            >
              {isSubmitting ? (
                <>
                  <span className={styles.spinner} />
                  {translations.submitting}
                </>
              ) : (
                translations.submit
              )}
            </button>
          </form>
        )}

        {!success && (
          <>
            <div className={styles.divider}>{translations.orDivider}</div>
            <GoogleSignInButton label={translations.googleSignUp} referral={referral} disabled={isSubmitting} />
            <FacebookSignInButton label={translations.facebookSignUp} referral={referral} disabled={isSubmitting} />
            <GithubSignInButton label={translations.githubSignUp} referral={referral} disabled={isSubmitting} />
            <TwitterSignInButton label={translations.twitterSignUp} referral={referral} disabled={isSubmitting} />
          </>
        )}

        <p className={styles.authFooter}>
          {translations.hasAccount}{' '}
          <Link href={localizeFullPath('/login')}>{translations.login}</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;
